'use client';

import dynamic from 'next/dynamic';
import React from 'react';

// Lazy load the ComparisonTable component (named export)
const LazyComparisonTable = dynamic(
  () => import('./ComparisonTable').then((mod) => ({ default: mod.ComparisonTable })),
  {
    ssr: false, // Client-side only for consistency
    loading: () => (
      <div className="w-full overflow-x-auto">
        <div className="min-w-[640px] bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="animate-pulse p-6">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
            </div>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="grid grid-cols-3 gap-4 mb-3">
                <div className="h-3 bg-gray-100 rounded"></div>
                <div className="h-3 bg-gray-100 rounded"></div>
                <div className="h-3 bg-gray-100 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }
);

// Wrapper with intersection observer for true lazy loading
export default function LazyComparisonTableWrapper({ children, ...props }: any) {
  const [shouldLoad, setShouldLoad] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      {
        // Load when component is 400px away from viewport (earlier for tables)
        rootMargin: '400px',
        threshold: 0
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef}>
      {shouldLoad ? (
        <LazyComparisonTable {...props}>
          {children}
        </LazyComparisonTable>
      ) : (
        <div className="w-full overflow-x-auto">
          <div className="min-w-[640px] bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-gray-300 text-sm text-center">
              <p>Comparison table loading...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}